/**
 * The read-along script: the sentences a spoken brief is made of, and where the voice is in them.
 *
 * **Why an estimate rather than real timings.** Neither synthesizer we use returns word or sentence
 * alignment — Gemini's streaming TTS yields PCM and nothing else, and Grok's REST endpoint the same.
 * So the position is derived from the one thing we do know: speech spends time roughly in proportion
 * to the characters it is reading. Mapping elapsed audio onto cumulative character weight puts the
 * marker within a word or two of the voice, which is what "don't lose me" needs; claiming per-word
 * accuracy we cannot measure would be worse than a sentence-sized honest one.
 *
 * Kept free of imports so the same module serves the route that builds the script and the browser that
 * follows it, and so the native ports (`BriefScript.swift` / `BriefScript.kt`) have one small thing to
 * mirror rather than a sanitizer, a selector and a splitter each.
 */

/**
 * What a sentence break costs, expressed in characters.
 *
 * A synthesizer pauses between sentences, so time is NOT purely proportional to characters — without
 * this the marker runs ahead by roughly the sum of those pauses, which over a brief is seconds by the
 * end.
 *
 * **Measured, not guessed.** A real 92.44s brief (1374 characters, 23 sentences) carries 17.76s of
 * internal silence across 40 breaks — so 0.77s per sentence boundary, which at its 18.7 char/s
 * speaking rate is ~14 characters. Fifteen, rounded. The first cut of this was 12 and the marker
 * visibly led the voice.
 */
export const SEGMENT_PAUSE_WEIGHT = 15;

/**
 * Silence before the first word, in seconds — time the marker must NOT spend.
 *
 * Gemini's stream opens with a lead-in (0.78s on the brief measured above). Counting it means the
 * marker sets off before the voice does and stays that far ahead for the whole brief, which reads as
 * "the highlight is faster than the reading" — because it is.
 */
export const SPEECH_LEAD_IN_SECONDS = 0.75;

/**
 * Silence after the last word (0.42s measured).
 *
 * Excluded for the same reason at the other end: charged for, it makes the marker crawl the final
 * sentence and land on the last word late.
 */
export const SPEECH_TAIL_SECONDS = 0.4;

/**
 * How far ahead of the ear the audio clock runs.
 *
 * Every clock available to us reports audio handed to the OUTPUT — `AudioContext.currentTime`, iOS's
 * `playerTime`, Android's `playbackHeadPosition` — and the device plays that out one buffer later. Tens
 * of milliseconds on a wired output, a few hundred over Bluetooth. Each platform can be asked for its
 * own figure, and if this proves too crude that is the upgrade; one shared allowance keeps the three
 * surfaces identical in the meantime, which matters more than the last 50ms.
 */
export const MARKER_PLAYOUT_ALLOWANCE_SECONDS = 0.2;

/**
 * The speaking rate, used ONLY until the real duration is known.
 *
 * The stream's true length arrives when it ends, which — measured — is a few seconds into a brief that
 * plays for over a minute, because generation outruns playback. So this covers the opening moments and
 * is then replaced by the truth; see `speechDurationEstimate`.
 */
export const ESTIMATED_CHARS_PER_SECOND = 15;

/**
 * A segment this long gets split further at a word boundary.
 *
 * Not a speech concern — a reading one. A 400-character run with no punctuation would highlight as one
 * undifferentiated block for half a minute, which is exactly the "where are we" problem this feature
 * exists to solve.
 */
export const MAX_SEGMENT_CHARACTERS = 220;

const TERMINATORS = new Set(['.', '!', '?', '…', '。', '！', '？']);
/** Closing punctuation that belongs to the sentence it ends — a break comes after it, not before. */
const TRAILING = new Set(['"', "'", '”', '’', ')', ']', '»']);
/** Tokens whose trailing period is not a sentence end. A single letter covers initials ("J. Smith"). */
const ABBREVIATION = /(?:^|[\s(])(?:[\p{L}]|mr|mrs|ms|dr|prof|sr|jr|st|vs|etc|approx|e\.g|i\.e|a\.m|p\.m|no)$/iu;

/**
 * Split spoken text into the sentences the highlight moves through.
 *
 * The segments are the RENDERED units too, joined by a single space — the paragraph breaks of the
 * written summary are deliberately not preserved, because speech has none and the script is a
 * transcript of what is being said, not a copy of the card.
 */
export function splitSpeechSegments(text: string): string[] {
  const segments: string[] = [];
  // Speech has no line breaks, but the written summary does, and a line is always a boundary: after
  // `sanitizeForSpeech` the leftover lines are list items, which a synthesizer reads as separate
  // sentences whether or not they carry a full stop.
  for (const line of text.split('\n')) {
    for (const sentence of splitSentences(line.trim())) {
      pushBounded(segments, sentence);
    }
  }
  return segments;
}

function splitSentences(line: string): string[] {
  if (!line) return [];
  const out: string[] = [];
  let start = 0;
  for (let i = 0; i < line.length; i++) {
    if (!TERMINATORS.has(line[i])) continue;
    let end = i + 1;
    while (end < line.length && (TERMINATORS.has(line[end]) || TRAILING.has(line[end]))) end++;
    if (end < line.length && !/\s/.test(line[end])) continue; // "3.5", "wix.com"
    if (line[i] === '.' && ABBREVIATION.test(line.slice(start, i))) continue;
    // A lowercase Latin letter after the break says the sentence carried on ("etc. and then…").
    // Scripts without case — Hebrew, Arabic, CJK — have no such signal and are unaffected.
    const next = line.slice(end).trimStart()[0];
    if (next && /\p{Ll}/u.test(next)) continue;
    const sentence = line.slice(start, end).trim();
    if (sentence) out.push(sentence);
    start = end;
    i = end - 1;
  }
  const tail = line.slice(start).trim();
  if (tail) out.push(tail);
  return out;
}

function pushBounded(into: string[], sentence: string): void {
  let rest = sentence;
  while (rest.length > MAX_SEGMENT_CHARACTERS) {
    const window = rest.slice(0, MAX_SEGMENT_CHARACTERS);
    const cut = window.lastIndexOf(' ');
    if (cut <= 0) break; // one unbroken word — splitting mid-word would read as a typo
    into.push(window.slice(0, cut).trim());
    rest = rest.slice(cut + 1);
  }
  if (rest.trim()) into.push(rest.trim());
}

export function segmentWeight(segment: string): number {
  return segment.length + SEGMENT_PAUSE_WEIGHT;
}

export interface SpeechPosition {
  /** The segment being spoken. `-1` only when there is no script at all. */
  index: number;
  /** How far into that segment the voice is, in whole characters — for anything that indexes text. */
  charactersRead: number;
  /**
   * The same position UNROUNDED.
   *
   * A character is ~55ms of speech, so a marker that can only sit on character boundaries steps rather
   * than sweeps. The fraction lets the highlight's edge land mid-character, which is what makes it read as
   * a pen drawn across the line instead of a cursor hopping along it.
   */
  charactersReadExact: number;
}

/**
 * Where the voice is, from the audio clock.
 *
 * `elapsedSeconds` must come from the AUDIO clock (the graph's own rendered time), never a wall clock:
 * playback starts late, stalls on a slow network and stops while paused, and a wall clock does none of
 * those things — the marker would drift away from the voice with nothing to pull it back.
 */
export function speechPosition(elapsedSeconds: number, totalSeconds: number, segments: string[]): SpeechPosition {
  if (segments.length === 0) return { index: -1, charactersRead: 0, charactersReadExact: 0 };
  if (!(totalSeconds > 0)) return { index: 0, charactersRead: 0, charactersReadExact: 0 };

  const weights = segments.map(segmentWeight);
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  // The marker moves through the SPOKEN window, not the whole file: the lead-in and the tail are
  // silence, and time spent in them is not time spent reading. Falling back to half the duration keeps
  // a very short brief (or an early estimate) from producing a zero or negative window.
  const spoken = Math.max(totalSeconds - SPEECH_LEAD_IN_SECONDS - SPEECH_TAIL_SECONDS, totalSeconds / 2);
  const heard = elapsedSeconds - SPEECH_LEAD_IN_SECONDS - MARKER_PLAYOUT_ALLOWANCE_SECONDS;
  const fraction = Math.min(Math.max(heard / spoken, 0), 1);
  let target = fraction * total;

  for (let i = 0; i < segments.length; i++) {
    if (target < weights[i] || i === segments.length - 1) {
      const within = Math.min(Math.max(target / weights[i], 0), 1);
      const exact = Math.min(segments[i].length, within * segments[i].length);
      return { index: i, charactersRead: Math.round(exact), charactersReadExact: exact };
    }
    target -= weights[i];
  }
  const last = segments.length - 1;
  return { index: last, charactersRead: segments[last].length, charactersReadExact: segments[last].length };
}

/**
 * The word the marker sits on, and the text either side of it.
 *
 * A whole-sentence highlight answers "which sentence"; inside a sentence that runs for several seconds
 * it still leaves the eye searching. Snapping to the enclosing WORD rather than cutting at the character
 * is what keeps that honest — a cut mid-word would advertise a precision the estimate does not have.
 *
 * **This marks the word; it does NOT bound the trail.** The trail's edge is `charactersRead` itself, to
 * the character, so it wipes smoothly across a word instead of jumping a word at a time — snapping it to
 * word boundaries made the highlight sit still for a second and then lurch, which reads as stuck. The
 * word this returns is the one the voice is inside, and it carries the marker on top of that edge.
 */
export function wordAt(segment: string, charactersRead: number): { before: string; word: string; after: string } {
  const cursor = Math.min(Math.max(charactersRead, 0), segment.length);
  let start = cursor;
  while (start > 0 && !/\s/.test(segment[start - 1])) start--;
  let end = cursor;
  while (end < segment.length && !/\s/.test(segment[end])) end++;
  return { before: segment.slice(0, start), word: segment.slice(start, end), after: segment.slice(end) };
}

/**
 * What a phrase break costs, in characters — the comma-sized sibling of `SEGMENT_PAUSE_WEIGHT`.
 *
 * A synthesizer breathes at commas too, just for less time: measured across two renders, roughly 0.2–0.3s
 * against 0.7–1.0s at a sentence end.
 */
export const PHRASE_PAUSE_WEIGHT = 6;

/** A stretch of near-silence in the rendered audio, in seconds from the first sample. */
export interface SpeechGap {
  start: number;
  end: number;
}

/** Below this RMS (full scale 1.0) the audio is silence. Gemini's silence is near-digital-zero. */
export const SILENCE_RMS = 0.01;
/** Shorter than this is a stop consonant, not a pause. Comma pauses run ~0.2s, sentence breaks ~0.4–1s. */
export const MIN_GAP_SECONDS = 0.16;
/** How far a boundary may be pulled from its character-weighted estimate to reach a real pause. */
export const GAP_SNAP_TOLERANCE_SECONDS = 2.5;

/**
 * A unit of TIMING — a phrase, not a sentence.
 *
 * **Why finer than a sentence.** The alignment can only be as precise as its anchors, and inside an anchor
 * the position is interpolated at an averaged rate. A 115-character sentence with one anchor therefore
 * drifts by a second or more in the middle, which is exactly what it looked like: the voice reached "two
 * lines…" while the marker was still 28 characters back. But the audio offers far more anchors than
 * sentences do — a real brief measured 50 pauses against 23 sentences, because a synthesizer breathes at
 * commas, colons and dashes. Splitting there turns each anchor's span into a second or two, where
 * character-proportional interpolation is accurate to a word.
 *
 * `offset` is into the JOINED script (sentences plus their single joining spaces), which is the coordinate
 * space every renderer already draws in.
 */
export interface TimingUnit {
  offset: number;
  length: number;
  /** A sentence end waits longer than a comma, so the prior charges it more. */
  endsSentence: boolean;
}

/** Where a synthesizer breathes: sentence ends, and the punctuation inside a sentence. */
const PHRASE_BREAK = /[,;:—]|\s-\s/;

/**
 * Split the script into the units the marker is timed against — phrases, in joined-script coordinates.
 *
 * Rendering is untouched: the text is the same string, and the trail is still one continuous run. This
 * only changes what the clock is compared against.
 */
export function timingUnits(segments: string[]): TimingUnit[] {
  const units: TimingUnit[] = [];
  let base = 0;
  for (const segment of segments) {
    let start = 0;
    for (let i = 0; i < segment.length; i++) {
      if (!PHRASE_BREAK.test(segment[i]) && !(segment[i] === '-' && segment[i - 1] === ' ')) continue;
      // Break AFTER the punctuation, and only where a space follows — "3.5" and "e-mail" are not breaths.
      let end = i + 1;
      while (end < segment.length && /[,;:—-]/.test(segment[end])) end++;
      if (end < segment.length && !/\s/.test(segment[end])) continue;
      if (end - start < 8) continue; // too short to time against on its own
      units.push({ offset: base + start, length: end - start, endsSentence: false });
      start = end;
      i = end - 1;
    }
    if (start < segment.length) {
      units.push({ offset: base + start, length: segment.length - start, endsSentence: true });
    } else if (units.length > 0) {
      units[units.length - 1].endsSentence = true;
    }
    base += segment.length + 1; // the joining space
  }
  return units;
}

export function unitWeight(unit: TimingUnit): number {
  return unit.length + (unit.endsSentence ? SEGMENT_PAUSE_WEIGHT : PHRASE_PAUSE_WEIGHT);
}

/**
 * One unit's measured place in the audio: when the voice starts it, and when it stops talking (which is
 * before the next one begins, because a pause sits between them).
 */
export interface SpeechSpan {
  start: number;
  speechEnd: number;
}

/**
 * Where each sentence actually falls in the audio — measured from its own silences where possible.
 *
 * **Why measure rather than estimate.** Two renders of the identical brief came out 92.4s and 98.5s, with
 * lead-in silence of 0.78s and 0.32s and internal pauses totalling 17.8s and 23.5s. The synthesizer varies
 * ~7% run to run, so a fixed chars/second is wrong by a growing margin — the marker gains on the voice for
 * the whole brief, which is exactly how it looked.
 *
 * The character weights still supply the PRIOR: they say roughly where a boundary should fall, and the
 * measured pauses say exactly where one is. Each boundary snaps to the nearest real pause within
 * `GAP_SNAP_TOLERANCE_SECONDS`, in order, never reusing one — a brief has more pauses than sentences
 * (commas breathe too), so "take the biggest gaps" would pick the wrong ones. Out of tolerance the prior
 * stands, which is also what happens with no gaps at all — a stream still generating, where the estimate
 * is all there is.
 */
export function alignUnits(units: TimingUnit[], gaps: SpeechGap[], totalSeconds: number): SpeechSpan[] {
  if (units.length === 0) return [];
  const weights = units.map(unitWeight);
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  // A gap that starts at zero IS the lead-in, and its end is when the voice really starts.
  const speechStart = gaps.length > 0 && gaps[0].start <= 0.001 ? gaps[0].end : SPEECH_LEAD_IN_SECONDS;
  const window = Math.max(totalSeconds - speechStart - SPEECH_TAIL_SECONDS, totalSeconds / 2);

  // The prior: where the character weights say each break falls, spread over the whole spoken window. It
  // stays GLOBAL on purpose. An incremental prior that learned the rate from each snap was measurably worse
  // — one break landing on the wrong pause inflates the rate, which pushes the next prior later, which
  // snaps to an even later pause. On a real 98.5s brief that runaway put the last sentences at 118.9s.
  const priors: number[] = [];
  let cumulative = 0;
  for (let i = 0; i < units.length - 1; i++) {
    cumulative += weights[i];
    priors.push(speechStart + (cumulative / total) * window);
  }
  const assigned = assignGaps(priors, gaps);

  const starts = [speechStart];
  const speechEnds: number[] = [];
  for (let i = 0; i < priors.length; i++) {
    const gap = assigned[i] >= 0 ? gaps[assigned[i]] : null;
    if (gap) {
      // The voice stops at the pause's start and the next unit opens at its end. Treating the pause as part
      // of either one is the same "ahead of the voice" error in miniature.
      speechEnds.push(Math.max(gap.start, starts[i]));
      starts.push(gap.end);
    } else {
      speechEnds.push(Math.max(priors[i], starts[i]));
      starts.push(Math.max(priors[i], starts[i]));
    }
  }
  speechEnds.push(Math.max(totalSeconds - SPEECH_TAIL_SECONDS, starts[starts.length - 1]));
  return starts.map((start, i) => ({ start, speechEnd: Math.max(speechEnds[i], start) }));
}

/**
 * The marker's offset into the joined script, from the audio clock and the measured spans.
 *
 * Within a unit the position is interpolated by character over that unit's SPEECH — a phrase runs a second
 * or two, where proportional interpolation is accurate to a word — so the pause after it is spent sitting
 * on its last word rather than walking into text nobody has heard yet.
 */
export function alignedOffset(
  elapsedSeconds: number,
  spans: SpeechSpan[],
  units: TimingUnit[],
  gaps: SpeechGap[],
): number {
  if (units.length === 0 || spans.length !== units.length) return 0;
  const heard = elapsedSeconds - MARKER_PLAYOUT_ALLOWANCE_SECONDS;
  let index = 0;
  while (index + 1 < spans.length && heard >= spans[index + 1].start) index++;

  const start = spans[index].start;
  const end = Math.max(spans[index].speechEnd, start);
  // **Interpolated over SPEECH time, not wall time.** A unit can hold silences of its own — "(Consult →
  // Plan → Train)" is read with pauses inside it — and a marker that keeps moving through those gains on the
  // voice, arrives at the unit's end early, waits there, and then covers the next unit all at once. That is
  // the "it waits, then colours a whole sentence" report. Discounting the silence makes the marker move only
  // while the voice does, which is what keeping pace means.
  const spoken = speechSeconds(start, Math.min(Math.max(heard, start), end), gaps);
  const total = speechSeconds(start, end, gaps);
  const within = total > 0 ? Math.min(Math.max(spoken / total, 0), 1) : 0;
  return units[index].offset + within * units[index].length;
}

/** Seconds of actual speech between two times — the span minus any measured silence inside it. */
export function speechSeconds(from: number, to: number, gaps: SpeechGap[]): number {
  if (to <= from) return 0;
  let silence = 0;
  for (const gap of gaps) {
    if (gap.end <= from) continue;
    if (gap.start >= to) break;
    silence += Math.min(gap.end, to) - Math.max(gap.start, from);
  }
  return Math.max(to - from - silence, 0);
}

/**
 * How quickly the marker closes a gap between where it is drawn and where the audio says the voice is.
 *
 * A quarter of a second: long enough that a correction reads as movement rather than a jump, short enough
 * that the marker is never visibly behind.
 */
export const MARKER_FOLLOW_TAU_SECONDS = 0.25;

/**
 * Ease the drawn marker toward the measured one instead of snapping to it.
 *
 * The alignment is exact at its anchors and interpolated in between, so a correction at an anchor arrives as
 * a jump. A first-order lag glides into it instead. Capped at the target so the marker never runs ahead of
 * the voice, and floored at where it already is so it never goes back.
 */
export function followOffset(drawn: number, target: number, deltaSeconds: number): number {
  if (deltaSeconds <= 0 || target <= drawn) return drawn;
  const alpha = 1 - Math.exp(-deltaSeconds / MARKER_FOLLOW_TAU_SECONDS);
  return Math.min(target, drawn + (target - drawn) * alpha);
}

/** A boundary that snaps to no pause costs this much, in tolerance units — cheaper than a bad snap. */
const SKIP_COST = 0.9;
/** A pause at least this long is a plausible sentence break; shorter ones are penalised as commas. */
const SENTENCE_PAUSE_SECONDS = 0.7;

/**
 * Which pause (if any) opens each sentence — chosen for the script as a WHOLE, not one boundary at a time.
 *
 * **Why a global assignment.** A brief carries far more pauses than sentences — 50 gaps for 23 sentences on
 * the brief this was measured against, one every two seconds — so "snap to the nearest pause" almost always
 * finds *a* pause, and picking a comma shifts everything after it. This is the standard monotonic
 * assignment: boundaries in order, pauses in order, each used at most once, minimising total cost. 22×50
 * cells, so the cost is nothing.
 *
 * Cost is distance from the prior (in tolerance units) plus a penalty for a pause too short to be a
 * sentence break — that is what breaks the tie between a long breath and a nearby comma. Skipping is
 * allowed and priced, so a sentence whose break the synthesizer simply did not take keeps its estimate
 * instead of stealing a neighbour's pause.
 */
function assignGaps(priors: number[], gaps: SpeechGap[]): number[] {
  const boundaries = priors.length;
  if (boundaries === 0 || gaps.length === 0) return priors.map(() => -1);

  const cost = (b: number, g: number): number => {
    const distance = Math.abs(gaps[g].start - priors[b]) / GAP_SNAP_TOLERANCE_SECONDS;
    if (distance > 1) return Infinity;
    const length = gaps[g].end - gaps[g].start;
    const shortness = Math.max(0, 1 - length / SENTENCE_PAUSE_SECONDS);
    return distance + shortness * 0.6;
  };

  // best[b][g] = least cost for boundaries 0..b-1 using only gaps 0..g-1.
  const best: number[][] = Array.from({ length: boundaries + 1 }, () => new Array(gaps.length + 1).fill(Infinity));
  const from: number[][] = Array.from({ length: boundaries + 1 }, () => new Array(gaps.length + 1).fill(0));
  for (let g = 0; g <= gaps.length; g++) best[0][g] = 0;

  for (let b = 1; b <= boundaries; b++) {
    for (let g = 0; g <= gaps.length; g++) {
      // 1: this boundary takes no pause.
      let value = best[b - 1][g] + SKIP_COST;
      let choice = 1;
      // 2: leave gap g-1 unused.
      if (g > 0 && best[b][g - 1] < value) {
        value = best[b][g - 1];
        choice = 2;
      }
      // 3: this boundary takes gap g-1.
      if (g > 0) {
        const take = best[b - 1][g - 1] + cost(b - 1, g - 1);
        if (take < value) {
          value = take;
          choice = 3;
        }
      }
      best[b][g] = value;
      from[b][g] = choice;
    }
  }

  const assigned = priors.map(() => -1);
  let b = boundaries;
  let g = gaps.length;
  while (b > 0) {
    const choice = from[b][g];
    if (choice === 2) {
      g--;
    } else if (choice === 3) {
      assigned[b - 1] = g - 1;
      b--;
      g--;
    } else {
      b--;
    }
  }
  return assigned;
}

/**
 * Where the voice is, given the measured spans.
 *
 * Within a sentence the position is still interpolated by character — over a second or two of speech that
 * is accurate to a word, and it is what makes the marker move smoothly rather than jumping per sentence.
 * The interpolation runs over the sentence's SPEECH, so the pause after it is spent sitting on its last
 * word instead of walking into text nobody has heard yet.
 */
export function alignedSpeechPosition(
  elapsedSeconds: number,
  spans: SpeechSpan[],
  segments: string[],
): SpeechPosition {
  if (segments.length === 0 || spans.length !== segments.length) {
    return { index: -1, charactersRead: 0, charactersReadExact: 0 };
  }
  const heard = elapsedSeconds - MARKER_PLAYOUT_ALLOWANCE_SECONDS;
  let index = 0;
  while (index + 1 < spans.length && heard >= spans[index + 1].start) index++;
  const span = Math.max(spans[index].speechEnd - spans[index].start, 0.001);
  const within = Math.min(Math.max((heard - spans[index].start) / span, 0), 1);
  const length = segments[index].length;
  const exact = Math.min(length, within * length);
  return { index, charactersRead: Math.round(exact), charactersReadExact: exact };
}

/**
 * The position as ONE offset into the whole joined script — earlier sentences, the single spaces joining
 * them, and the fraction into the live one.
 *
 * The renderers need a single number to clamp: sentence-and-character is two, and comparing pairs invites
 * the bug this exists to prevent.
 */
export function globalOffsetExact(segments: string[], position: SpeechPosition): number {
  if (position.index < 0 || position.index >= segments.length) return 0;
  let offset = 0;
  for (let i = 0; i < position.index; i++) offset += segments[i].length + 1;
  return offset + Math.min(Math.max(position.charactersReadExact, 0), segments[position.index].length);
}

/** The inverse: which sentence that offset is in, and how far into it. */
export function positionFromGlobalOffset(segments: string[], offset: number): SpeechPosition {
  if (segments.length === 0) return { index: -1, charactersRead: 0, charactersReadExact: 0 };
  let remaining = Math.max(offset, 0);
  for (let i = 0; i < segments.length; i++) {
    const length = segments[i].length;
    if (remaining <= length || i === segments.length - 1) {
      const exact = Math.min(Math.max(remaining, 0), length);
      return { index: i, charactersRead: Math.round(exact), charactersReadExact: exact };
    }
    remaining -= length + 1; // the joining space
  }
  const last = segments.length - 1;
  return { index: last, charactersRead: segments[last].length, charactersReadExact: segments[last].length };
}

/**
 * The marker, held to never moving backwards.
 *
 * **Measured, not theoretical.** A screen recording of a real brief showed the trail retreating fifty times
 * in eighteen seconds — about three times a second — which reads as bumpiness. The cause is not the clock
 * (that is monotonic) but the ALIGNMENT: every newly measured silence re-runs the assignment, and a
 * boundary that shifts later drags the current sentence's start with it, so the position computes lower
 * than it did a frame ago. Re-aligning is right — the alignment genuinely improves as the audio arrives —
 * but the visible marker must not un-read words the listener has already heard.
 *
 * `previous` is the caller's high-water mark, in the units of `globalOffsetExact`.
 */
export function monotonicPosition(segments: string[], position: SpeechPosition, previous: number): SpeechPosition {
  const offset = globalOffsetExact(segments, position);
  if (offset >= previous) return position;
  return positionFromGlobalOffset(segments, previous);
}

/**
 * How long the audio runs — the truth once the stream has ended, an estimate before that.
 *
 * `bufferedSeconds` is how much audio has actually been received, so at end-of-stream it IS the
 * duration; there is nothing left to arrive. Before that it is a floor rather than a guess, hence the
 * `max`: the rate estimate can be wrong, but audio already in hand cannot be.
 */
export function speechDurationEstimate(options: {
  segments: string[];
  bufferedSeconds: number;
  streamEnded: boolean;
}): number {
  const { segments, bufferedSeconds, streamEnded } = options;
  if (streamEnded) return bufferedSeconds;
  const characters = segments.reduce((sum, segment) => sum + segment.length, 0);
  return Math.max(bufferedSeconds, characters / ESTIMATED_CHARS_PER_SECOND);
}
